import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlanner } from '../../hooks/PlannerContext';
import { clampProgressIndex } from '../../utilities/activitySelection';
import ActivityDetailsModal from './ActivityDetailsModal';
import './ActivitySessionProgressBar.css';

const getActivityNumber = (selectedActivity) => {
  const match = typeof selectedActivity === 'string'
    ? selectedActivity.match(/^learning-path-(\d+)$/)
    : null;
  return match ? match[1] : null;
};

// Matches the server's COMPLETION_THRESHOLD (functions/common/activityProgress.js):
// an attempt at 95%+ completes the activity, so the bar turns green and stays
// green (attempt progress is monotonic within a session).
const SESSION_COMPLETION_THRESHOLD = 95;

// The celebration starts once the fill has visually reached the end of the
// bar (width transition duration) and clears itself after the confetti falls.
const CELEBRATION_DELAY_MS = 500;
const CELEBRATION_DURATION_MS = 1800;
const CONFETTI_PIECES = 12;

function ActivitySessionProgressBar({
  progressIndex = 0,
  selectedActivity = null,
  conversationProgress = null,
  activityDoc = null,
}) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const celebratedRef = useRef(false);
  const plannerContext = usePlanner();

  const normalizedProgress = clampProgressIndex(progressIndex);
  const isComplete = normalizedProgress >= SESSION_COMPLETION_THRESHOLD;
  const label = t('activity_progress_label');
  const activityNumber = getActivityNumber(selectedActivity);
  const activity = activityNumber
    ? plannerContext?.currentPlannerData?.[activityNumber] || null
    : null;

  // Celebrate once per attempt when the bar completes: wait for the fill to
  // reach the end, then pop the value with a burst of tiny confetti.
  useEffect(() => {
    if (!isComplete) {
      celebratedRef.current = false; // a fresh attempt resets the celebration
      return undefined;
    }
    if (celebratedRef.current) {
      return undefined;
    }

    celebratedRef.current = true;
    const startTimer = setTimeout(() => setCelebrate(true), CELEBRATION_DELAY_MS);
    const stopTimer = setTimeout(() => setCelebrate(false), CELEBRATION_DELAY_MS + CELEBRATION_DURATION_MS);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(stopTimer);
    };
  }, [isComplete]);

  const valueClassName = [
    'activity-session-progress__value',
    isComplete ? 'activity-session-progress__value--complete' : '',
    celebrate ? 'activity-session-progress__value--celebrate' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="activity-session-progress-anchor">
      <div className="activity-session-progress" aria-label={label}>
        {/* Plain divs instead of <progress>: Chromium doesn't reliably paint
            custom backgrounds inside ::-webkit-progress-value. */}
        <div
          className="activity-session-progress__bar"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={normalizedProgress}
          aria-valuetext={`${normalizedProgress}%`}
        >
          <div
            className={`activity-session-progress__bar-fill${isComplete ? ' activity-session-progress__bar-fill--complete' : ''}`}
            style={{ width: `${normalizedProgress}%` }}
          />
        </div>
        {/* Keyed by value so each change replays the small pop animation */}
        <span key={normalizedProgress} className={valueClassName}>
          {normalizedProgress}%
          {celebrate && (
            <span className="activity-session-progress__confetti" aria-hidden="true">
              {Array.from({ length: CONFETTI_PIECES }, (_, index) => <i key={index} />)}
            </span>
          )}
        </span>
        {activity && (
          <button
            type="button"
            className="activity-session-progress__details-button"
            onClick={() => setShowDetails(true)}
          >
            {t('activity_details_button')}
          </button>
        )}
      </div>

      <ActivityDetailsModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        activity={activity}
        attemptProgressIndex={normalizedProgress}
        conversationProgress={conversationProgress}
        activityDoc={activityDoc}
      />
    </div>
  );
}

export default ActivitySessionProgressBar;
